import { useEffect, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { Users, UserPlus, Trash2, Mail, Loader2 } from "lucide-react";
import { db } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { useProjects } from "@contexts/ProjectContext";
import InfoBentoCard from "@ui/InfoBentoCard";

function MemberList({ project }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    const ref = collection(db, "projects", project.id, "members");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          // The owner has a member doc too — don't show them in the
          // "invited collaborators" list.
          .filter((m) => m.role !== "owner");
        list.sort((a, b) => (a.invitedAt || 0) - (b.invitedAt || 0));
        setMembers(list);
        setLoading(false);
      },
      (err) => {
        console.error("[ProjectSharing] members snapshot", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [user, project.id]);

  const handleInvite = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address");
      return;
    }
    if (trimmed === (user.email || "").toLowerCase()) {
      setError("You can't invite yourself");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const ref = doc(
        db,
        "projects",
        project.id,
        "members",
        `email_${trimmed}`
      );
      await setDoc(ref, {
        subjectType: "email",
        subjectId: trimmed,
        email: trimmed,
        role: "editor",
        invitedBy: user.uid,
        invitedAt: Date.now(),
        joinedAt: null,
      });
      setEmail("");
    } catch (err) {
      console.error("[ProjectSharing] invite failed", err);
      setError(err.message || "Failed to add member");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (memberId) => {
    const label = members.find((m) => m.id === memberId)?.email || memberId;
    if (!window.confirm(`Remove ${label} from "${project.name}"?`)) return;
    try {
      const ref = doc(db, "projects", project.id, "members", memberId);
      await deleteDoc(ref);
    } catch (err) {
      console.error("[ProjectSharing] remove failed", err);
    }
  };

  return (
    <div
      className="rounded-lg p-4 mb-3"
      style={{ background: "var(--bg-input)", border: "1px solid var(--border-input)" }}
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: "var(--text-heading)" }}>
            {project.name}
          </p>
          {project.address && (
            <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>
              {project.address}
            </p>
          )}
        </div>
        <div
          className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 flex-shrink-0"
          style={{ color: "var(--text-secondary)" }}
        >
          <Users className="w-3 h-3" />
          {members.length} {members.length === 1 ? "member" : "members"}
        </div>
      </div>

      {loading ? (
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          Loading members…
        </p>
      ) : members.length === 0 ? (
        <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
          No one else has access yet.
        </p>
      ) : (
        <div className="space-y-1.5 mb-3">
          {members.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md"
              style={{ background: "rgba(0,0,0,0.1)" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                <span className="text-xs truncate" style={{ color: "var(--text-heading)" }}>
                  {m.email || m.subjectId}
                </span>
                <span
                  className="text-[9px] uppercase tracking-wider font-semibold flex-shrink-0"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {m.role || "editor"}
                </span>
              </div>
              <button
                onClick={() => handleRemove(m.id)}
                className="p-1 rounded opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: "var(--text-secondary)" }}
                aria-label={`Remove ${m.email || m.subjectId}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleInvite} className="flex items-center gap-2 flex-wrap">
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          placeholder="collaborator@example.com"
          className="flex-1 min-w-[200px] text-xs rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          style={{
            background: "rgba(0,0,0,0.12)",
            border: "1px solid var(--border-input)",
            color: "var(--text-heading)",
          }}
        />
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors"
        >
          {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
          Add
        </button>
      </form>
      {error && (
        <p className="text-xs mt-2" style={{ color: "#ef4444" }}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function ProjectSharing() {
  const { projects } = useProjects();
  const ownedProjects = projects.filter((p) => !p.shared);

  return (
    <InfoBentoCard cornerGlow frostedGradient accent="emerald" className="mb-6">
      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">
        Sharing
      </p>
      <p className="text-sm text-gray-500 mb-4">
        Invite collaborators by email. Once they create an account with that address, your
        project appears in their sidebar automatically. No email is sent from the app —
        share the signup link with them directly.
      </p>

      {ownedProjects.length === 0 ? (
        <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
          No projects to share yet.
        </p>
      ) : (
        ownedProjects.map((p) => <MemberList key={`${p.ownerUid}|${p.id}`} project={p} />)
      )}
    </InfoBentoCard>
  );
}
